import { motion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  ChevronRight,
  Loader2,
  Mail,
  Trash2,
  User,
} from "lucide-react";
import { useTran } from "@/hooks";
import { SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";
import { Card, CardContent } from "@/components/ui/card";

const formatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const PaginationControls = ({
  page,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTran();
  const pageSizeOptions = [5, 10, 20, 50].map((opt) => ({
    key: String(opt),
    label: String(opt),
  }));
  return (
    <div className="flex flex-col items-center gap-3 pt-3 sm:flex-row sm:justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{t("Rows per page")}:</span>
        <SelectField
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange?.(Number(value) || 10)}
          options={pageSizeOptions}
          className="w-20 space-y-0"
          selectClassName="h-8"
        />
      </div>
      <div className="flex items-center gap-3">
        <Button
          size="sm"
          variant="outline"
          className="rounded-full bg-transparent"
          disabled={page <= 1}
          onClick={() => onPageChange?.(Math.max(1, page - 1))}
        >
          {t("Previous")}
        </Button>
        <span className="text-sm text-muted-foreground">
          {t("Page")} {page} {t("of")} {Math.max(1, totalPages)}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full bg-transparent"
          disabled={page >= totalPages}
          onClick={() => onPageChange?.(Math.min(totalPages || 1, page + 1))}
        >
          {t("Next")}
        </Button>
      </div>
    </div>
  );
};

const SurveyResponseList = ({
  responses = [],
  loading = false,
  page = 1,
  totalPages = 1,
  pageSize = 10,
  onPageSizeChange,
  onPageChange,
  onView,
  onDelete,
}) => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-radius border border-border bg-card/80 p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          {t("Loading responses...")}
        </div>
      </div>
    );
  }

  if (!responses.length) {
    return (
      <div className="space-y-3">
        <div className="rounded-radius border border-dashed border-border bg-card/60 p-6 text-center text-sm text-muted-foreground">
          {t(
            "No responses yet. Start taking surveys to build your knowledge history.",
          )}
        </div>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {responses.map((response) => {
          const surveyTitle = response?.survey?.title || t("Survey");
          const surveyDescription = response?.survey?.description || "";
          const submittedLabel = response?.submittedAt
            ? formatter.format(new Date(response.submittedAt))
            : t("Unknown date");
          const duration = response?.completionTimeInSeconds
            ? `${Math.max(1, Math.round(response.completionTimeInSeconds / 60))} min`
            : null;
          const hasInvitation =
            (response.inviterName && response.inviterName.trim() !== "") ||
            response.inviterEmail;

          return (
            <motion.div
              key={response._id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="group relative overflow-hidden rounded-radius border border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
                <div className="flex h-full flex-col">
                  {/* Header Section */}
                  <div className="space-y-2  border-border p-5">
                    <h3 className="truncate text-xl font-semibold text-foreground">
                      {surveyTitle}
                    </h3>
                    {surveyDescription ? (
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {surveyDescription}
                      </p>
                    ) : null}
                  </div>

                  {/* Content Section */}
                  <CardContent className="flex-1 p-5">
                    {hasInvitation ? (
                      <div className="rounded-radius bg-muted/40 p-4">
                        <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
                          <div className="flex flex-1 items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                              <User className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-muted-foreground">
                                {t("Invited by")}
                              </p>
                              <p className="truncate text-sm font-semibold text-foreground">
                                {response.inviterName || t("Unknown")}
                              </p>
                              {response.inviterEmail ? (
                                <p className="truncate text-xs text-muted-foreground">
                                  {response.inviterEmail}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <motion.div
                            animate={{ x: [0, 4, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className="flex-shrink-0"
                          >
                            <ArrowRight className="h-5 w-5 text-muted-foreground" />
                          </motion.div>

                          <div className="flex flex-1 items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                              <Mail className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-muted-foreground">
                                {t("Responded")}
                              </p>
                              <p className="truncate text-sm font-semibold text-foreground">
                                {response.name || t("Anonymous")}
                              </p>
                              {response.email ? (
                                <p className="truncate text-xs text-muted-foreground">
                                  {response.email}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-radius bg-muted/40 p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-muted-foreground">
                              {t("Responded")}
                            </p>
                            <p className="truncate text-sm font-semibold text-foreground">
                              {response.name || t("Anonymous")}
                            </p>
                            {response.email ? (
                              <p className="truncate text-xs text-muted-foreground">
                                {response.email}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>

                  {/* Footer Section */}
                  <div className=" border-border p-5">
                    <div className="space-y-3">
                      {/* Metadata Badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-3 py-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {submittedLabel}
                        </span>
                        {duration ? (
                          <span className="rounded-full bg-muted/60 px-3 py-1 text-xs text-muted-foreground">
                            {duration}
                          </span>
                        ) : null}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Can
                          resource="surveys"
                          action="view"
                          userPermissions={permissions}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-transparent flex-1"
                            onClick={() => onView?.(response)}
                          >
                            {t("View Detail")}
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Can>
                        <Can
                          resource="surveys"
                          action="delete"
                          userPermissions={permissions}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => onDelete?.(response)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </Can>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
export default SurveyResponseList;
